<script setup lang="ts">
import { computed, type LabelHTMLAttributes, useAttrs } from 'vue';

export interface KaLabelProps {
  label?: string;
  hideLabel?: boolean;
  for?: LabelHTMLAttributes['for'];
  form?: LabelHTMLAttributes['form'];
  required?: boolean;
  ariaRequired?: boolean;
  /** Only use this property if you plan to add the asterisk in the slot content of the label.
   * A required field MUST have a visual indicator to be accessible.
   * https://www.w3.org/WAI/WCAG22/Techniques/aria/ARIA2
   * https://veeam.hub.essentia11y.com/manual-evaluations/67af37db16faa72414aa1705/results/AMP256/view?skipScanStandards=true&evaluationStandard=WCAG_v22_AAA&status=toReview,open,partiallyFixed,notFixed,cannotBeFixed,validationRequested&issueId=AMP256&linkedPropertyData=6762fe26f5d3a5eaed29f2c8%7C6723d15450ee91f82f056d41
   */
  hideRequiredAsterisk?: boolean;
}

const props = withDefaults(defineProps<KaLabelProps>(), {
  hideLabel: false,
  required: false,
});

const attrs = useAttrs();
const dataTestid = (attrs['data-testid'] as string) || (attrs.id as string);
const requiredAsteriskTestid = dataTestid ? `${dataTestid}-required-asterisk` : undefined;
const isRequired = computed(() => props.required || props.ariaRequired);
</script>
<template>
  <label
    :data-testid="dataTestid"
    :for="props.for"
    :form="props.form"
    :class="[
      'flex items-center text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50',
      { 'sr-only': props.hideLabel },
    ]"
  >
    <slot v-bind="{ label: props.label }">{{ props.label }}</slot>
    <!--
      The asterisk is the visual required indicator only. Hide it from assistive tech (aria-hidden):
      required state is conveyed programmatically via aria-required on the associated control, so a
      screen reader would otherwise read a redundant "star". Kept in the DOM/accessible name's
      textContent for sighted users; excluded from the accessible name of any control that labels
      itself from this element.
    -->
    <span
      v-if="isRequired && !props.hideRequiredAsterisk"
      aria-hidden="true"
      :data-testid="requiredAsteriskTestid"
      >&nbsp;*</span
    >
  </label>
</template>
